/**
 * Connection runner for the global SSE (Server-Sent Events) base layer.
 *
 * This is the central unit of the streaming layer: it owns the `fetch`-based
 * connection lifecycle, bearer-header auth + one-shot token refresh, reconnect
 * with full-jitter exponential backoff, `Last-Event-ID` resume, stale/connect
 * timeouts, visibility-aware reconnection, and idempotent teardown.
 *
 * It builds on three pure units (do not duplicate their logic here):
 * - `SSEParser` — bytes -> SSE frames.
 * - `classifyResponse` / `classifyError` — HTTP/error -> next action.
 * - the public `types` contract.
 *
 * See `docs/superpowers/specs/2026-06-01-sse-base-layer-design.md`.
 */

import { useApiStore } from 'src/core/stores/api';
import { useUserStore } from 'src/core/stores/user';
import {
  classifyError,
  classifyResponse,
  type ResponseAction,
} from 'src/core/services/eventStream/classify';
import { SSEParser } from 'src/core/services/eventStream/parser';
import type {
  EventStreamCloseReason,
  EventStreamConnection,
  EventStreamOptions,
  EventStreamStatus,
  RawEventStreamMessage,
} from 'src/core/services/eventStream/types';

/** Default reconnect/backoff and timeout tuning. */
const DEFAULT_BASE_DELAY_MS = 1000;
const DEFAULT_MAX_DELAY_MS = 30000;
const DEFAULT_MAX_RETRIES = Infinity;
const DEFAULT_CONNECT_MS = 20000;
const DEFAULT_STALE_MS = 0;

/** Whether a thrown value is an `AbortError` (our timeout/lifecycle aborts). */
function isAbortError(err: unknown): boolean {
  return (err as { name?: string } | null | undefined)?.name === 'AbortError';
}

/**
 * Open and run a typed event stream. Connection starts immediately; the
 * returned handle exposes live status and an idempotent `close()`.
 *
 * @typeParam TEvent - The feature's typed event, produced by `parse`.
 */
export function createEventStream<TEvent>(
  options: EventStreamOptions<TEvent>
): EventStreamConnection {
  const {
    url,
    method = 'GET',
    body,
    onMessage,
    onOpen,
    onReconnect,
    onError,
    onClose,
    onStatusChange,
    headers: staticHeaders,
    lastEventIdParam,
  } = options;

  const parse =
    options.parse ??
    ((raw: RawEventStreamMessage) => JSON.parse(raw.data) as TEvent);

  const baseDelayMs = options.reconnect?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const maxDelayMs = options.reconnect?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
  const maxRetries = options.reconnect?.maxRetries ?? DEFAULT_MAX_RETRIES;
  const connectMs = options.timeouts?.connectMs ?? DEFAULT_CONNECT_MS;
  const staleMs = options.timeouts?.staleMs ?? DEFAULT_STALE_MS;

  // --- Explicit, minimal state -------------------------------------------
  let status: EventStreamStatus = 'connecting';
  let lastEventId: string | null = options.lastEventId ?? null;
  let attempt = 0; // consecutive failed reconnects
  let closed = false; // terminal guard
  let refreshedThisCycle = false; // reset on every successful open

  // Whole-connection lifecycle controller; aborted exactly once in finish().
  const lifecycle = new AbortController();
  // Per-attempt controller; replaced each loop iteration.
  let abortCurrent: AbortController = new AbortController();

  function setStatus(next: EventStreamStatus): void {
    if (status === next) {
      return;
    }
    status = next;
    onStatusChange?.(next);
  }

  // --- URL + headers ------------------------------------------------------

  /** Resolve the API base URL the same way the shared axios instance does. */
  function apiBase(): string {
    return (
      useApiStore().service.defaults.baseURL ||
      import.meta.env.VITE_API_URL ||
      ''
    );
  }

  /** Build the request URL, recomputed every (re)connect for the resume param. */
  function buildUrl(): string {
    const isAbsolute = /^https?:\/\//i.test(url);
    const resolved = isAbsolute ? url : apiBase() + url;

    if (lastEventIdParam == null || lastEventId == null) {
      return resolved;
    }

    const u = new URL(resolved, window.location.origin);
    u.searchParams.set(lastEventIdParam, lastEventId);
    return u.toString();
  }

  /**
   * Build request headers. Managed headers (`Content-Type`, `Accept`,
   * `Authorization`, `Last-Event-ID`) are spread last so they always win over
   * caller `headers`. `Content-Type: application/json` is set only when a JSON
   * body is sent (non-GET request with a body).
   */
  function buildHeaders(
    token: string,
    jsonBody: boolean
  ): Record<string, string> {
    return {
      ...staticHeaders,
      ...(jsonBody ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(lastEventId != null ? { 'Last-Event-ID': lastEventId } : {}),
    };
  }

  // --- Auth ---------------------------------------------------------------

  /** Refresh the access token once. Returns whether a new token was obtained. */
  async function refresh(): Promise<boolean> {
    const us = useUserStore();
    if (!us.refresh_token) {
      return false;
    }
    const data = await us.refreshToken({ refresh_token: us.refresh_token });
    return Boolean(data?.access_token);
  }

  // --- Timing helpers -----------------------------------------------------

  /** Full-jitter exponential backoff for attempt `n` (1-based). */
  function backoff(n: number): number {
    return Math.random() * Math.min(maxDelayMs, baseDelayMs * 2 ** (n - 1));
  }

  /** A timed delay that also resolves early if the lifecycle aborts. */
  function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        lifecycle.signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      if (lifecycle.signal.aborted) {
        onAbort();
        return;
      }
      lifecycle.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  /**
   * Resolve once the document is visible (or immediately if there is no
   * document / it is already visible), or early if the lifecycle aborts.
   */
  function waitUntilVisible(): Promise<void> {
    if (typeof document === 'undefined' || !document.hidden) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const cleanup = () => {
        document.removeEventListener('visibilitychange', onVisibility);
        lifecycle.signal.removeEventListener('abort', onAbort);
      };
      const onVisibility = () => {
        if (!document.hidden) {
          cleanup();
          resolve();
        }
      };
      const onAbort = () => {
        cleanup();
        resolve();
      };
      document.addEventListener('visibilitychange', onVisibility);
      lifecycle.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  // --- Stream consumption -------------------------------------------------

  /** Commit a frame's resume id, then hand the parsed event to the consumer. */
  function dispatch(frame: RawEventStreamMessage): void {
    if (frame.id !== null) {
      lastEventId = frame.id;
    }
    try {
      onMessage(parse(frame));
    } catch (err) {
      // A parse/handler throw must NOT drop the connection.
      onError?.(err);
    }
  }

  /**
   * Drain the response body until clean EOF (returns) or an abort/network
   * failure (throws). Resets the optional stale timer on every chunk of bytes.
   */
  async function consumeStream(
    body: NonNullable<Response['body']>
  ): Promise<void> {
    const reader = body.getReader();
    const parser = new SSEParser();
    let staleTimer: ReturnType<typeof setTimeout> | null = null;

    const armStale = () => {
      if (staleMs <= 0) {
        return;
      }
      if (staleTimer !== null) {
        clearTimeout(staleTimer);
      }
      staleTimer = setTimeout(() => abortCurrent.abort(), staleMs);
    };

    try {
      armStale();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          for (const frame of parser.end()) {
            dispatch(frame);
          }
          break;
        }
        armStale();
        for (const frame of parser.feed(value as Uint8Array)) {
          dispatch(frame);
        }
      }
    } finally {
      if (staleTimer !== null) {
        clearTimeout(staleTimer);
      }
      reader.releaseLock?.();
    }
  }

  /**
   * Perform one connect attempt and, if it opens a stream, consume it to EOF.
   * Returns the action the run loop should take next; throws on abort/network.
   */
  async function openAndConsume(): Promise<ResponseAction> {
    setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

    const token = useUserStore().access_token;

    const connectTimer =
      connectMs > 0 ? setTimeout(() => abortCurrent.abort(), connectMs) : null;

    // A request body only rides on non-GET requests (fetch forbids a GET body).
    // It is JSON-serialised here and resent verbatim on every (re)connect.
    const hasBody = method !== 'GET' && body != null;

    let response: Response;
    try {
      response = (await fetch(buildUrl(), {
        method,
        headers: buildHeaders(token, hasBody),
        ...(hasBody ? { body: JSON.stringify(body) } : {}),
        signal: abortCurrent.signal,
      })) as Response;
    } finally {
      if (connectTimer !== null) {
        clearTimeout(connectTimer);
      }
    }

    const action = classifyResponse(response);
    if (action.kind !== 'stream') {
      // complete / refresh / fatal / retry are handled by run().
      return action;
    }

    // Healthy open: reset backoff and the one-shot refresh cycle.
    attempt = 0;
    refreshedThisCycle = false;
    setStatus('open');
    onOpen?.();

    await consumeStream(response.body as NonNullable<Response['body']>);

    // Clean EOF -> reconnect (a 204 on the next connect will end it).
    return { kind: 'retry' };
  }

  // --- Teardown -----------------------------------------------------------

  /** Reach the terminal `closed` state exactly once. */
  function finish(reason: EventStreamCloseReason): void {
    if (closed) {
      return;
    }
    closed = true;
    setStatus('closed');
    abortCurrent?.abort();
    lifecycle.abort();
    onClose?.(reason);
  }

  // --- The run loop -------------------------------------------------------

  async function run(): Promise<void> {
    while (!closed) {
      abortCurrent = new AbortController();

      // Relay lifecycle aborts onto the current attempt.
      const relay = () => abortCurrent.abort();
      if (lifecycle.signal.aborted) {
        relay();
      } else {
        lifecycle.signal.addEventListener('abort', relay, { once: true });
      }

      let action: ResponseAction;
      try {
        action = await openAndConsume();
      } catch (err) {
        if (closed) {
          break;
        }
        // Our own timeout/lifecycle aborts are transient -> retry.
        action = isAbortError(err) ? { kind: 'retry' } : classifyError(err);
      } finally {
        lifecycle.signal.removeEventListener('abort', relay);
      }

      if (closed) {
        break;
      }

      switch (action.kind) {
        case 'complete':
          finish('completed');
          return;

        case 'fatal':
          finish('forbidden');
          return;

        case 'refresh': {
          if (refreshedThisCycle) {
            finish('unauthorized');
            return;
          }
          refreshedThisCycle = true;
          if (!(await refresh())) {
            finish('unauthorized');
            return;
          }
          // Immediate retry: no backoff, do not increment attempt.
          continue;
        }

        default: {
          // 'retry' — also a clean server EOF.
          attempt += 1;
          if (attempt > maxRetries) {
            finish('retries-exhausted');
            return;
          }
          setStatus('reconnecting');
          onReconnect?.(lastEventId, attempt);
          // Backoff first, THEN gate on visibility (spec §7): a visible tab
          // reconnects after the normal backoff; a hidden tab finishes its
          // backoff then connects immediately on becoming visible (no
          // background fetch).
          await delay(action.retryAfterMs ?? backoff(attempt));
          if (closed) {
            return;
          }
          await waitUntilVisible();
          if (closed) {
            return;
          }
        }
      }
    }
  }

  void run();

  return {
    get status() {
      return status;
    },
    get lastEventId() {
      return lastEventId;
    },
    close() {
      finish('manual');
    },
  };
}
